/**
 * Org-name display guards (#1457).
 *
 * A half-created / pending org can carry a sentinel placeholder in its name fields —
 * `__BW_PENDING__:business_name` / `__BW_PENDING__:legal_name` — left behind by an
 * abandoned onboarding draft (junk staging data; production onboarding never writes
 * it). The platform FE renders org names verbatim across the Dashboard, Approvals,
 * Audit, Onboarding, and detail surfaces, so without a guard the raw token leaks
 * into the UI. These helpers normalize an org's display name so a sentinel (or a
 * blank value) degrades to a friendly human label instead of the raw token.
 *
 * Presentation-layer hardening only — the authoritative fix is data hygiene at the
 * seed/source. The helpers are intentionally tiny and read-only.
 */

/** The exact prefix a pending/placeholder name starts with. */
const PENDING_SENTINEL_PREFIX = "__BW_PENDING__";

/** The friendly stand-in shown for an org whose business name isn't set yet. */
export const PENDING_BUSINESS_LABEL = "Pending business";

type OrgNameFields = {
  business_name?: string | null;
  legal_name?: string | null;
};

/** True when a value is the pending sentinel (exact prefix) — not merely contains it. */
function isPendingSentinel(value: string): boolean {
  return value.startsWith(PENDING_SENTINEL_PREFIX);
}

/**
 * The org's business name for display. Returns the trimmed real name, or the
 * friendly {@link PENDING_BUSINESS_LABEL} when the name is the pending sentinel,
 * blank, or absent — never the raw `__BW_PENDING__:…` token.
 */
export function orgBusinessName(org: OrgNameFields): string {
  const trimmed = org.business_name?.trim();
  if (!trimmed || isPendingSentinel(trimmed)) return PENDING_BUSINESS_LABEL;
  return trimmed;
}

/**
 * The org's legal name for display, or `null` when it is the pending sentinel,
 * blank, or absent. Legal name is optional in the UI, so the caller owns the
 * "not provided" presentation (some surfaces render "Legal name not provided");
 * this only ensures the raw sentinel token is never shown.
 */
export function orgLegalName(org: OrgNameFields): string | null {
  const trimmed = org.legal_name?.trim();
  if (!trimmed || isPendingSentinel(trimmed)) return null;
  return trimmed;
}
